// --- src/services/inventoryService.ts ---
import { supabase } from '@/lib/supabaseClient';
import {
  InventoryMap,
  SupplierMap,
  SupplierProduct,
  ScanMode,
  TransactionItemInput,
  TransactionResult,
} from '../components/types';
import { safeParseOfflineUser, safeJSONParse, safeParseArray } from '../utils/safeParse';
import { retry, dbCircuitBreaker, dbRateLimiter, shouldRetryDatabaseError } from '../utils/retry';
import { cacheService, CacheKeys } from './cacheService';
import { adminService } from './adminService';
import { syncService } from './syncService';
import { getRpcName as getRpcNameFromUtil, RPC_FUNCTIONS } from '../utils/rpc';

export interface InventoryGlobalStats {
  stock_value: number;
  counted_items: number;
  total_items: number;
  not_scanned: number;
  unknown: number;
  low_stock_count: number;
  shopping_list_count: number;
}

// Queue item interface for offline synchronization
interface QueueItem {
  id: string;
  type: 'updateItemQuantity' | 'logAction';
  timestamp: number;
  data: {
    // For updateItemQuantity
    ean?: string;
    delta?: number;
    productDetails?: SupplierProduct;
    reason?: ScanMode;
    // For logAction
    actionType?: string;
    details?: string;
  };
}

// localStorage keys
const QUEUE_KEY = 'inventory_sync_queue';
const CACHE_KEY_SUPPLIER = 'cached_supplier_data';
const CACHE_KEY_INVENTORY = 'cached_inventory_data';

const getRpcName = (baseName: string): string => {
  const schema = import.meta.env.VITE_SUPABASE_SCHEMA;
  // If schema is 'leltarmester_prod', return baseName + '_prod'
  // If schema is 'leltarmester_dev', return baseName + '_dev'
  // Default fallback: baseName + '_prod'
  if (schema === 'leltarmester_dev') return `${baseName}_dev`;
  if (schema === 'leltarmester_prod') return `${baseName}_prod`;
  return `${baseName}_prod`;
};

export const inventoryService = {
  // ===== OFFLINE MODE & SYNC QUEUE =====

  /**
   * Adds a failed operation to the sync queue
   */
  addToQueue(item: Omit<QueueItem, 'id' | 'timestamp'>): void {
    try {
      const queue = this.getQueue();
      const queueItem: QueueItem = {
        ...item,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: Date.now(),
      };
      queue.push(queueItem);
      localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
      // [Sync] Added to sync queue
    } catch (error) {
      console.error('Error adding to queue:', error);
    }
  },

  /**
   * Gets the current sync queue from localStorage
   */
  getQueue(): QueueItem[] {
    const stored = localStorage.getItem(QUEUE_KEY);
    return safeParseArray(stored, []);
  },

  /**
   * Processes all items in the sync queue
   * NOTE: This function calls logAction which should be moved to a separate service or passed as dependency
   */
  async processQueue(
    logAction?: (actionType: string, details: string, isSyncing: boolean) => Promise<void>
  ): Promise<void> {
    const queue = this.getQueue();
    if (queue.length === 0) {
      // [Sync] Queue is empty
      return;
    }

    // [Sync] Processing items from sync queue

    // CRITICAL: Refresh/validate session before processing
    let user = null;
    try {
      const { data, error: authError } = await supabase.auth.getUser();
      if (data?.user) {
        user = data.user;
      }
    } catch (error) {
      // Auth check failed, try cached user
    }

    // Fallback to cached user if auth failed (offline mode)
    if (!user) {
      const cachedUserStr = localStorage.getItem('offline_user');
      const cachedUser = safeParseOfflineUser(cachedUserStr);
      if (cachedUser) {
        user = { id: cachedUser.id } as any;
        console.log('[processQueue] Using cached user for sync');
      }
    }

    if (!user) {
      console.error('processQueue: No user session - cannot sync');
      throw new Error('No user session available');
    }
    // [Sync] Session validated

    // Process items one by one, removing successful ones immediately
    let processedCount = 0;
    let failedCount = 0;

    for (const item of queue) {
      try {
        if (item.type === 'updateItemQuantity') {
          const result = await this.updateItemQuantity(
            item.data.ean!,
            item.data.delta!,
            item.data.productDetails,
            item.data.reason || 'COUNT',
            true // isSyncing = true
          );

          if (result !== null) {
            // Success - remove from queue immediately
            this.removeFromQueue(item.id);
            processedCount++;
            // [Sync] Successfully synced item
          } else {
            // Returned null (might be offline or other issue)
            failedCount++;
            console.warn(
              `⚠ updateItemQuantity returned null for EAN: ${item.data.ean} (keeping in queue)`
            );
          }
        } else if (item.type === 'logAction') {
          if (logAction) {
            await logAction(
              item.data.actionType!,
              item.data.details!,
              true // isSyncing = true
            );

            // Success - remove from queue immediately
            this.removeFromQueue(item.id);
            processedCount++;
            console.log(
              `✓ Successfully synced logAction: ${item.data.actionType} (removed from queue)`
            );
          } else {
            console.warn('logAction not available in processQueue - skipping logAction items');
          }
        }
      } catch (error: any) {
        // Better error logging
        const errorMessage = error?.message || String(error);
        const errorCode = error?.code || 'UNKNOWN';

        // Check if this is a fatal error (business logic error) that should not be retried
        const isFatalError =
          errorMessage?.includes('Insufficient stock') ||
          errorMessage?.includes('P0001') ||
          errorCode === 'P0001' ||
          errorMessage?.includes('violates row-level security') ||
          errorMessage?.includes('row-level security') ||
          (errorCode && errorCode.startsWith('P') && errorCode.length === 5); // PostgreSQL error codes (P0001, P0002, etc.)

        if (isFatalError) {
          // Fatal error: remove from queue permanently and notify user
          this.removeFromQueue(item.id);
          failedCount++;

          console.error(`✗ FATAL ERROR - Removed queue item ${item.id} (type: ${item.type}):`, {
            error: errorMessage,
            code: errorCode,
            item: item.data,
          });

          // Show user-friendly error message
          // Import toast dynamically to avoid circular dependencies
          import('react-hot-toast')
            .then(({ toast: toastImport }) => {
              toastImport.error(
                'Szinkronizálási hiba: A tranzakciót elutasította a szerver (pl. készlethiány). A tétel törölve a sorból.',
                {
                  duration: 6000,
                  icon: '⚠️',
                }
              );
            })
            .catch((importError) => {
              console.warn('Could not import toast for error notification:', importError);
            });
        } else {
          // Network error or other retryable error: keep in queue for next retry
          failedCount++;
          console.error(
            `✗ Failed to sync queue item ${item.id} (type: ${item.type}) - will retry:`,
            {
              error: errorMessage,
              code: errorCode,
              item: item.data,
            }
          );
          // Keep failed items in queue for next retry (they're still in localStorage)
        }
      }
    }

    // [Sync] Sync complete
  },

  /**
   * Removes a specific item from the queue by ID
   */
  removeFromQueue(itemId: string): void {
    try {
      const queue = this.getQueue();
      const filteredQueue = queue.filter((item) => item.id !== itemId);
      localStorage.setItem(QUEUE_KEY, JSON.stringify(filteredQueue));
    } catch (error) {
      console.error('Error removing item from queue:', error);
    }
  },

  /**
   * Gets the current size of the sync queue
   */
  getQueueSize(): number {
    return this.getQueue().length;
  },

  // 1. ADATOK LETÖLTÉSE
  // PRODUCTION-READY: Uses caching, retry, rate limiting, and circuit breaker
  async fetchAllData(
    page: number = 1,
    pageSize: number = 50,
    searchTerm?: string,
    filterMode: 'ALL' | 'LOW_STOCK' | 'SHOPPING_LIST' = 'ALL'
  ): Promise<{ supplierMap: SupplierMap; inventoryMap: InventoryMap; totalCount: number }> {
    try {
      // Get user with offline fallback
      let user = null;
      try {
        const { data, error: authError } = await supabase.auth.getUser();
        if (data?.user) {
          user = data.user;
        }
      } catch (error) {
        // Auth check failed, try cached user
      }

      // Fallback to cached user if auth failed (offline mode)
      if (!user) {
        const cachedUserStr = localStorage.getItem('offline_user');
        const cachedUser = safeParseOfflineUser(cachedUserStr);
        if (cachedUser) {
          user = { id: cachedUser.id } as any;
        }
      }

      if (!user) {
        // Try to return cached data if no user
        const cached = this.getCachedData();
        // Apply search filter to cached data if offline
        if (searchTerm && searchTerm.trim()) {
          const filtered = this.filterCachedDataBySearch(cached, searchTerm);
          return { ...filtered, totalCount: Object.keys(filtered.inventoryMap).length };
        }
        return { ...cached, totalCount: Object.keys(cached.inventoryMap).length };
      }

      // Check cache first (2 minute TTL for inventory data)
      // CRITICAL: Skip cache when searching or filtering to ensure fresh results
      const cacheKey = CacheKeys.inventory(page, pageSize);
      if (!searchTerm?.trim() && filterMode === 'ALL') {
        const cached = cacheService.get<{
          supplierMap: SupplierMap;
          inventoryMap: InventoryMap;
          totalCount: number;
        }>(cacheKey);
        if (cached) {
          console.log(`[fetchAllData] ✅ Cache HIT for page ${page}`);
          return cached;
        }
      } else {
        console.log(`[fetchAllData] 🔍 SEARCH/FILTER MODE: Skipping cache for fresh results`);
      }

      console.log(`[fetchAllData] Cache MISS, fetching page ${page} from database...`, {
        userId: user.id,
        page,
        pageSize,
        searchTerm: searchTerm || '',
        filterMode,
      });

      // Rate limiting - prevent overwhelming the database
      await dbRateLimiter.check();

      // CRITICAL: Use RPC function for server-side filtering and pagination
      const { data: rpcResponse, error: rpcError } = await dbCircuitBreaker.execute(async () => {
        return await retry(
          async () => {
            const rpcName = getRpcName('get_inventory_paginated');
            const { data, error } = await supabase.rpc(rpcName, {
              p_page: page,
              p_page_size: pageSize,
              p_search: searchTerm || '',
              p_filter_mode: filterMode || 'ALL',
            });

            if (error) {
              console.error(`[fetchAllData] RPC error:`, error);
              throw error;
            }

            return { data, error: null };
          },
          {
            maxRetries: 2,
            delay: 500,
            shouldRetry: shouldRetryDatabaseError,
          }
        );
      });

      if (rpcError) {
        console.error(`[fetchAllData] Failed to fetch data from RPC:`, rpcError);
        throw rpcError;
      }

      // Handle the response safely - RPC returns { data: [...], total_count: 123 }
      if (!rpcResponse || !rpcResponse.data || rpcResponse.data.length === 0) {
        console.log(`[fetchAllData] No data returned from RPC, returning empty maps`);
        return {
          supplierMap: {},
          inventoryMap: {},
          totalCount: rpcResponse?.total_count || 0,
        };
      }

      const supplierMap: SupplierMap = {};
      const inventoryMap: InventoryMap = {};

      console.log(
        `[fetchAllData] Processing ${rpcResponse.data.length} items from RPC (page ${page})`
      );

      // CRITICAL: Map the rows manually to fix the case mismatch
      // Iterate over rpcResponse.data and populate supplierMap and inventoryMap
      rpcResponse.data.forEach((row: any) => {
        const ean = row.ean;
        if (!ean) {
          console.warn(`[fetchAllData] Skipping row with missing EAN:`, row);
          return;
        }

        // Map snake_case DB columns to camelCase frontend properties
        // Supplier data (same for all users, just take the first one)
        if (!supplierMap[ean]) {
          supplierMap[ean] = {
            ean: ean,
            name: row.name || 'Ismeretlen',
            sku: row.sku || '',
            // Net prices (primary for profit calculations)
            netPrice: Number(row.net_price) || Number(row.price) || 0, // Fallback to price if net_price not set
            netSellingPrice: Number(row.net_selling_price) || Number(row.selling_price) || 0, // Fallback to selling_price if net_selling_price not set
            // Gross prices (for POS backward compatibility)
            sellingPrice: Number(row.selling_price) || 0, // Gross selling price (integer, rounded)
            vatRate: Number(row.vat_rate) || 27,
            isWeighable: row.is_weighable ?? false,
            unitWeight: row.unit_weight ? Number(row.unit_weight) : undefined,
            unit: row.unit || 'db',
          };
        }

        // Inventory data: Use the LATEST record (by last_scanned_at) for each EAN
        // Date Conversion: row.last_scanned_at (string) -> scannedAt (Date object)
        const scannedAt = row.last_scanned_at ? new Date(row.last_scanned_at) : new Date(0);

        // Only update if this is the first record for this EAN, or if it's newer
        if (!inventoryMap[ean] || scannedAt > inventoryMap[ean].scannedAt) {
          inventoryMap[ean] = {
            ean: ean,
            quantity: row.quantity ?? 0,
            scannedAt: scannedAt,
            min_quantity: row.min_quantity ?? null,
            on_shopping_list: row.on_shopping_list ?? false,
            isWeighable: row.is_weighable ?? false,
            unitWeight: row.unit_weight ? Number(row.unit_weight) : undefined,
            unit: row.unit || 'db',
            // Net pricing fields
            netPrice: row.net_price ? Number(row.net_price) : undefined,
            netSellingPrice: row.net_selling_price ? Number(row.net_selling_price) : undefined,
            vatRate: row.vat_rate ? Number(row.vat_rate) : undefined,
          };
        }
      });

      console.log(
        `[fetchAllData] Created inventoryMap with ${Object.keys(inventoryMap).length} items, supplierMap with ${Object.keys(supplierMap).length} items`
      );

      // Save to both localStorage (for offline) and memory cache (for speed)
      const result = {
        supplierMap,
        inventoryMap,
        totalCount: rpcResponse.total_count || 0,
      };

      try {
        // localStorage cache (for offline mode)
        // CRITICAL: Only cache unfiltered, non-search results to prevent stale data
        // When filterMode is LOW_STOCK or SHOPPING_LIST, the RPC returns a subset of items.
        // Caching that subset under the same key would cause the ALL view to show stale filtered data.
        if (!searchTerm?.trim() && filterMode === 'ALL') {
          localStorage.setItem(CACHE_KEY_SUPPLIER, JSON.stringify(supplierMap));
          localStorage.setItem(CACHE_KEY_INVENTORY, JSON.stringify(inventoryMap));

          // Memory cache with 2 minute TTL (for fast repeated access)
          cacheService.set(cacheKey, result, 2 * 60 * 1000); // 2 minutes

          console.log(`[fetchAllData] ✅ Data cached successfully (page ${page})`);
        } else {
          console.log(
            `[fetchAllData] 🔍 SEARCH/FILTER MODE: Skipping cache write (filter=${filterMode}, search=${searchTerm || '(none)'})`
          );
        }
      } catch (cacheError) {
        console.warn('[fetchAllData] Failed to cache data:', cacheError);
      }

      return result;
    } catch (error) {
      // Offline fallback: try to retrieve from cache
      console.warn('Failed to fetch data from Supabase, attempting to load from cache...', error);
      const cached = this.getCachedData();
      // Apply search filter to cached data if offline
      if (searchTerm && searchTerm.trim()) {
        const filtered = this.filterCachedDataBySearch(cached, searchTerm);
        return { ...filtered, totalCount: Object.keys(filtered.inventoryMap).length };
      }
      return { ...cached, totalCount: Object.keys(cached.inventoryMap).length };
    }
  },

  /**
   * Filters cached data by search term (offline fallback)
   */
  filterCachedDataBySearch(
    cached: { supplierMap: SupplierMap; inventoryMap: InventoryMap },
    searchTerm: string
  ): { supplierMap: SupplierMap; inventoryMap: InventoryMap } {
    const searchLower = searchTerm.toLowerCase().trim();
    const filteredSupplierMap: SupplierMap = {};
    const filteredInventoryMap: InventoryMap = {};

    Object.keys(cached.supplierMap).forEach((ean) => {
      const supplier = cached.supplierMap[ean];
      const inventory = cached.inventoryMap[ean];
      const matchesName = supplier?.name?.toLowerCase().includes(searchLower);
      const matchesEan = ean.includes(searchTerm);

      if (matchesName || matchesEan) {
        filteredSupplierMap[ean] = supplier;
        if (inventory) {
          filteredInventoryMap[ean] = inventory;
        }
      }
    });

    return { supplierMap: filteredSupplierMap, inventoryMap: filteredInventoryMap };
  },

  /**
   * Retrieves cached data from localStorage and converts scannedAt strings to Date objects
   */
  getCachedData(): { supplierMap: SupplierMap; inventoryMap: InventoryMap } {
    try {
      const cachedSupplier = localStorage.getItem(CACHE_KEY_SUPPLIER);
      const cachedInventory = localStorage.getItem(CACHE_KEY_INVENTORY);

      if (!cachedSupplier || !cachedInventory) {
        console.log('No cached data available');
        return { supplierMap: {}, inventoryMap: {} };
      }

      const supplierMap: SupplierMap = safeJSONParse(cachedSupplier, {});
      const inventoryMapRaw = safeJSONParse(cachedInventory, {});

      // Convert scannedAt strings back to Date objects
      const inventoryMap: InventoryMap = {};
      Object.keys(inventoryMapRaw).forEach((ean) => {
        const item = inventoryMapRaw[ean];
        inventoryMap[ean] = {
          ...item,
          scannedAt: item.scannedAt ? new Date(item.scannedAt) : new Date(),
        };
      });

      console.log('Loaded data from cache:', {
        supplierCount: Object.keys(supplierMap).length,
        inventoryCount: Object.keys(inventoryMap).length,
      });

      return { supplierMap, inventoryMap };
    } catch (error) {
      console.error('Error reading cached data:', error);
      return { supplierMap: {}, inventoryMap: {} };
    }
  },

  // 2. CSV IMPORTÁLÁS
  async uploadSupplierData(
    products: SupplierProduct[],
    logActionType: string = 'IMPORT'
  ): Promise<{
    totalProcessed: number;
    successCount: number;
    failedCount: number;
    errors: string[];
  }> {
    // Get user with offline fallback
    let user = null;
    try {
      const { data, error: authError } = await supabase.auth.getUser();
      if (data?.user) {
        user = data.user;
      }
    } catch (error) {
      // Auth check failed, try cached user
    }

    // Fallback to cached user if auth failed (offline mode)
    if (!user) {
      const cachedUserStr = localStorage.getItem('offline_user');
      if (cachedUserStr) {
        try {
          const cachedUser = JSON.parse(cachedUserStr);
          user = { id: cachedUser.id } as any;
        } catch (e) {
          // Failed to parse cached user
        }
      }
    }

    if (!user) throw new Error('Jelentkezz be az importáláshoz!');

    // 1. Get Company ID from profiles table
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', user.id)
      .single();

    if (profileError) {
      console.error('Error fetching company_id:', profileError);
      throw new Error('Hiba a vállalat azonosító lekérdezésekor!');
    }

    const companyId = profile?.company_id;

    // 2. Map fields including weight, company, and hybrid pricing
    // Note: price and selling_price are GROSS (for POS compatibility)
    // net_price and net_selling_price are NET (for profit calculations)
    const dbRows = products.map((p) => {
      const vatRate = p.vatRate ?? 27;
      // Calculate Net prices first (primary source of truth)
      const purchaseNet = p.netPrice || 0;
      const sellingNet = p.netSellingPrice || 0;
      // Calculate Gross from Net (2 decimal precision for sub-1 Ft prices)
      const purchaseGross =
        purchaseNet > 0 ? Math.round(purchaseNet * (1 + vatRate / 100) * 100) / 100 : 0;
      const sellingGross =
        sellingNet > 0
          ? Math.round(sellingNet * (1 + vatRate / 100) * 100) / 100
          : p.sellingPrice || 0;

      return {
        user_id: user.id,
        company_id: companyId,
        ean: p.ean,
        name: p.name,
        sku: p.sku,
        // Gross values (for POS backward compatibility)
        price: purchaseGross,
        selling_price: sellingGross,
        // Net values (for profit calculations)
        net_price: purchaseNet,
        net_selling_price: sellingNet,
        vat_rate: vatRate,
        // Map weight fields (convert camelCase to snake_case)
        is_weighable: p.isWeighable || false,
        unit_weight: p.unitWeight || 0,
        unit: p.unit || 'db',
        // CRITICAL FIX: Include min_quantity in creation payload (prevents race condition)
        // NULL = no limit, 0 = valid limit (alert when stock reaches 0)
        ...(p.minQuantity !== undefined && p.minQuantity !== null
          ? { min_quantity: p.minQuantity }
          : {}),
      };
    });

    // 3. Batch upload: Split into chunks of 50 to avoid 409 Conflict errors
    const BATCH_SIZE = 50;
    const totalBatches = Math.ceil(dbRows.length / BATCH_SIZE);
    const batchErrors: Array<{ batch: number; error: any }> = [];
    let successCount = 0;
    let failedCount = 0;
    const errorMessages: string[] = [];

    console.log(
      `[uploadSupplierData] Starting batch upload: ${dbRows.length} items in ${totalBatches} batches`
    );

    for (let i = 0; i < totalBatches; i++) {
      const start = i * BATCH_SIZE;
      const end = start + BATCH_SIZE;
      const batch = dbRows.slice(start, end);
      const batchNumber = i + 1;

      try {
        const { error } = await supabase
          .from('inventory_items')
          .upsert(batch, { onConflict: 'ean, user_id', ignoreDuplicates: false });

        if (error) {
          const errorMsg = `Batch ${batchNumber}/${totalBatches} hiba: ${error.message || JSON.stringify(error)}`;
          console.error(`[uploadSupplierData] ❌ ${errorMsg}`);
          batchErrors.push({ batch: batchNumber, error });
          failedCount += batch.length;
          errorMessages.push(errorMsg);
        } else {
          console.log(
            `[uploadSupplierData] ✅ Batch ${batchNumber}/${totalBatches} feltöltve (${batch.length} items)`
          );
          successCount += batch.length;
        }
      } catch (err: any) {
        const errorMsg = `Batch ${batchNumber}/${totalBatches} exception: ${err?.message || String(err)}`;
        console.error(`[uploadSupplierData] ❌ ${errorMsg}`);
        batchErrors.push({ batch: batchNumber, error: err });
        failedCount += batch.length;
        errorMessages.push(errorMsg);
      }
    }

    // 4. Return detailed report instead of throwing error
    const report = {
      totalProcessed: dbRows.length,
      successCount,
      failedCount,
      errors: errorMessages,
    };

    if (batchErrors.length > 0) {
      console.warn(
        `[uploadSupplierData] ⚠️ Partial failure: ${batchErrors.length} batch(es) failed out of ${totalBatches} total batches`
      );
    } else {
      console.log(`[uploadSupplierData] ✅ All ${totalBatches} batches uploaded successfully`);
    }

    // Log successful import - COUNT logs are created by updateItemQuantity via process_inventory_transaction RPC
    // Only log if IMPORT to avoid duplicate logging
    if (logActionType === 'IMPORT' && report.successCount > 0) {
      const logMessage = `Adatbázis import: ${report.successCount} termék sikeresen feltöltve${report.failedCount > 0 ? `, ${report.failedCount} sikertelen` : ''}`;
      await adminService.logAction(logActionType, logMessage, false);
    }

    return report;
  },

  async checkExistingProducts(eans: string[]): Promise<SupplierMap> {
    // Get user with offline fallback
    let user = null;
    try {
      const { data, error: authError } = await supabase.auth.getUser();
      if (data?.user) {
        user = data.user;
      }
    } catch (error) {
      // Auth check failed, try cached user
    }

    // Fallback to cached user if auth failed (offline mode)
    if (!user) {
      const cachedUserStr = localStorage.getItem('offline_user');
      if (cachedUserStr) {
        try {
          const cachedUser = JSON.parse(cachedUserStr);
          user = { id: cachedUser.id } as any;
        } catch (e) {
          // Failed to parse cached user
        }
      }
    }

    if (!user) return {};

    // Lekérjük azokat a termékeinket, amiknek az EAN-ja benne van a listában (RLS handles company_id filtering)
    const { data } = await supabase
      .from('inventory_items')
      .select('ean, name, sku, price, selling_price, net_price, net_selling_price, vat_rate')
      .in('ean', eans);

    const result: SupplierMap = {};
    data?.forEach((row: any) => {
      result[row.ean] = {
        ean: row.ean,
        name: row.name,
        sku: row.sku,
        netPrice: Number(row.net_price) || Number(row.price) || 0,
        sellingPrice: Number(row.selling_price) || 0,
        netSellingPrice: Number(row.net_selling_price) || Number(row.selling_price) || 0,
        vatRate: Number(row.vat_rate) || 27,
      };
    });

    return result;
  },

  // 3. SZKENNELÉS (REFACTORED: Uses RPC with server-side math)
  /**
   * Updates inventory quantity using the RPC function with server-side delta calculation.
   * This prevents race conditions by performing all math in a single SQL transaction.
   *
   * @param ean - Product EAN code
   * @param delta - The CHANGE amount (always positive, e.g., 1, 5). The RPC handles sign based on transaction type.
   * @param productDetails - Optional product details (name, prices)
   * @param reason - Transaction type (determines if delta is added or subtracted server-side)
   * @param isSyncing - Whether this is being called from the sync queue (prevents infinite queue loops)
   * @returns Promise with transaction result containing the new quantity from server
   */
  async updateItemQuantity(
    ean: string,
    delta: number,
    productDetails?: SupplierProduct,
    reason: ScanMode = 'COUNT',
    isSyncing: boolean = false,
    oldQuantity?: number, // Pass old quantity from frontend state to detect threshold crossing
    sellerName?: string // Optional seller name for logging
  ): Promise<TransactionResult | null> {
    // CRITICAL: Check network status first - if offline and not syncing, queue immediately
    if (!navigator.onLine && !isSyncing) {
      this.addToQueue({
        type: 'updateItemQuantity',
        data: {
          ean,
          delta,
          productDetails,
          reason,
        },
      });
      console.log('Offline detected - added to sync queue immediately');
      // Return null to indicate operation was queued (not an error state)
      return null;
    }

    // Validate delta (must be positive, RPC handles sign based on type)
    if (delta <= 0) {
      console.warn('updateItemQuantity: Delta must be positive, got:', delta);
      return null;
    }

    // FIXED PRICE CALCULATION LOGIC
    // 1. Extract values safely
    const vatRate = productDetails?.vatRate ?? 27;
    const netPrice = productDetails?.netPrice ?? 0;
    const sellingNet = productDetails?.netSellingPrice ?? 0;

    // 2. Calculate GROSS prices (Database expects 'price' and 'selling_price' to be GROSS)
    // 2 decimal precision for sub-1 Ft prices
    const grossPrice = netPrice > 0 ? Math.round(netPrice * (1 + vatRate / 100) * 100) / 100 : 0;

    const grossSelling =
      sellingNet > 0
        ? Math.round(sellingNet * (1 + vatRate / 100) * 100) / 100
        : (productDetails?.sellingPrice ?? 0);

    console.log('[updateItemQuantity] Price Debug:', {
      in_net: netPrice,
      in_gross: 'N/A',
      out_gross_calculated: grossPrice, // This MUST be sent to DB 'price'
      vat: vatRate,
    });

    // 3. Construct Payload
    const item: TransactionItemInput = {
      ean: ean,
      quantity: delta,

      // CRITICAL FIX: Send calculated GROSS prices to the legacy fields
      price: grossPrice, // DB column 'price' is GROSS
      selling_price: grossSelling, // DB column 'selling_price' is GROSS

      // Send explicit NET prices for newer DB functions
      net_price: netPrice,
      net_selling_price: sellingNet,
      vat_rate: vatRate,

      // Other fields
      name: productDetails?.name,
      unit_weight: productDetails?.unitWeight,
      is_weighable: productDetails?.isWeighable,
      unit: productDetails?.unit || 'db',
    };

    try {
      // Get user with offline fallback
      let user = null;
      try {
        const { data, error: authError } = await supabase.auth.getUser();
        if (data?.user) {
          user = data.user;
        }
      } catch (error) {
        // Auth check failed, try cached user
      }

      // Fallback to cached user if auth failed (offline mode)
      if (!user) {
        const cachedUserStr = localStorage.getItem('offline_user');
        const cachedUser = safeParseOfflineUser(cachedUserStr);
        if (cachedUser) {
          user = { id: cachedUser.id } as any;
        }
      }

      if (!user) {
        // If no user but we're not syncing, queue the operation
        if (!isSyncing) {
          this.addToQueue({
            type: 'updateItemQuantity',
            data: {
              ean,
              delta,
              productDetails,
              reason,
            },
          });
          console.log('No user session - added to sync queue');
        }
        return null;
      }

      // Call RPC function (handles all math server-side in a transaction)
      console.log('🚀 RPC PAYLOAD DEBUG:', {
        ean: item.ean,
        sent_net_price: item.net_price,
        sent_gross_price: item.price,
        sent_vat: item.vat_rate,
        calculated_check: (item.net_price || 0) * (1 + (item.vat_rate || 27) / 100),
      });
      // CRITICAL: Use V2 version to bypass Supabase function cache bug
      const { data, error } = await supabase.rpc(
        getRpcNameFromUtil(RPC_FUNCTIONS.PROCESS_INVENTORY_TRANSACTION),
        {
          p_user_id: user.id,
          p_type: reason,
          p_notes: sellerName ? `Felhasználó: ${sellerName}` : null,
          p_items: [item],
        }
      );

      if (error) {
        console.error('RPC process_inventory_transaction error:', error);
        throw new Error(`Transaction failed: ${error.message}`);
      }

      if (!data) {
        console.warn('RPC returned no data');
        return null;
      }

      // CRITICAL: New Unified SQL Schema returns { updated_items: [...] }
      // Extract the first item from the updated_items array
      const response = data as {
        updated_items: Array<{ ean: string; new_quantity: number; updated_at: string }>;
      };

      if (!response.updated_items || response.updated_items.length === 0) {
        console.warn('RPC returned no updated_items');
        return null;
      }

      // Get the first updated item
      const updatedItem = response.updated_items[0];
      const result: TransactionResult = {
        transaction_id: '', // Not used in new schema
        ean: updatedItem.ean,
        new_quantity: updatedItem.new_quantity,
        updated_at: updatedItem.updated_at || new Date().toISOString(),
      };

      // CRITICAL: IMMEDIATELY AFTER successful transaction, fetch full item data
      // This ensures we have the most up-to-date quantity, min_quantity, and on_shopping_list
      // CRITICAL: Fetch company-wide quantity (latest record for this EAN in the company)
      // This ensures all terminals see the same global stock
      let itemData: any = null;
      let itemError: any = null;

      try {
        // Get company_id first
        const { data: userProfile } = await supabase
          .from('profiles')
          .select('company_id')
          .eq('id', user.id)
          .eq('is_active', true)
          .single();

        const companyId = userProfile?.company_id;
        let userIdsToFilter: string[] = [user.id];

        if (companyId) {
          const { data: companyUsers } = await supabase
            .from('profiles')
            .select('id')
            .eq('company_id', companyId)
            .eq('is_active', true);
          userIdsToFilter = companyUsers?.map((u) => u.id) || [user.id];
        }

        const fetchResult = await retry(
          async () => {
            // CRITICAL: Fetch company-wide quantity (latest record for this EAN)
            // Get all records for this EAN in the company, then take the latest one
            const { data: companyRecords, error: companyError } = await supabase
              .from('inventory_items')
              .select('quantity, min_quantity, on_shopping_list, name, last_scanned_at')
              .eq('ean', ean)
              .in('user_id', userIdsToFilter)
              .order('last_scanned_at', { ascending: false, nullsFirst: false })
              .limit(1)
              .maybeSingle();

            if (companyError) {
              // Fallback: Get current user's record
              const { data: userData, error: userError } = await supabase
                .from('inventory_items')
                .select('quantity, min_quantity, on_shopping_list, name')
                .eq('ean', ean)
                .eq('user_id', user.id)
                .single();

              if (userError) throw userError;
              return userData;
            }

            return companyRecords;
          },
          {
            maxRetries: 3,
            delay: 300,
            backoff: 'exponential',
            shouldRetry: (error: any) => {
              // Retry on network errors or 406/500 errors
              return error?.code === 'PGRST116' || error?.status === 406 || error?.status >= 500;
            },
          }
        );
        itemData = fetchResult;
      } catch (error: any) {
        itemError = error;
        console.warn('Failed to fetch item data after transaction (after retries):', error);
      }

      // If we couldn't fetch item data, use RPC result but calculate quantity from it
      // The RPC result contains new_quantity which should be correct
      let dbQuantity: number;
      let minQty: number | null;
      let isOnShoppingList: boolean;
      let productName: string;

      if (itemError || !itemData) {
        // Fallback: Use RPC result values, but verify they make sense
        console.warn(`[updateItemQuantity] Using RPC result for ${ean} (item fetch failed):`, {
          rpcNewQuantity: result.new_quantity,
          oldQuantity: oldQuantity,
          delta: delta,
          reason: reason,
        });

        // Calculate expected quantity based on transaction type
        let expectedQty: number;
        if (reason === 'SALE' || reason === 'SCRAP' || reason === 'INTERNAL') {
          // Subtraction: oldQuantity - delta
          expectedQty = Math.max(0, (oldQuantity ?? 0) - delta);
        } else {
          // Addition: oldQuantity + delta
          expectedQty = (oldQuantity ?? 0) + delta;
        }

        // Use RPC result if it's reasonable, otherwise use calculated value
        if (result.new_quantity !== undefined && result.new_quantity >= 0) {
          dbQuantity = result.new_quantity;
          // If RPC result is 0 but we expected more, log warning
          if (result.new_quantity === 0 && expectedQty > 0) {
            console.warn(
              `[updateItemQuantity] RPC returned 0 but expected ${expectedQty} for ${ean}, using calculated value`
            );
            dbQuantity = expectedQty;
          }
        } else {
          // RPC result is invalid, use calculated value
          console.warn(
            `[updateItemQuantity] RPC result invalid for ${ean}, using calculated value: ${expectedQty}`
          );
          dbQuantity = expectedQty;
        }

        // For fallback: null = no limit set
        minQty = null;
        isOnShoppingList = false;
        productName = productDetails?.name || ean;
      } else {
        // Extract values from database (use actual DB values, not RPC result)
        // min_quantity: NULL = no limit, 0+ = valid limit
        dbQuantity = itemData.quantity ?? 0;
        minQty = itemData.min_quantity != null ? itemData.min_quantity : null;
        isOnShoppingList = itemData.on_shopping_list ?? false;
        productName = productDetails?.name || itemData.name || ean;
      }

      // CRITICAL: Check if stock is low using the actual database values
      // NULL = no limit, 0 = valid limit (alert when stock reaches 0)
      const isLowStock = minQty !== null && dbQuantity <= minQty;

      // Check if we just crossed the threshold (for subtraction operations only)
      const isSubtractionMode = reason === 'SALE' || reason === 'SCRAP' || reason === 'INTERNAL';
      const justCrossedThreshold =
        minQty !== null &&
        isSubtractionMode &&
        oldQuantity !== undefined &&
        oldQuantity > minQty &&
        dbQuantity <= minQty;

      // AUTO-REMOVE FROM SHOPPING LIST: If stock increased and is now above min_quantity
      const isAdditionMode = reason === 'COUNT' || reason === 'RETURN';
      let removedFromShoppingList = false;

      if (isAdditionMode && isOnShoppingList && minQty !== null && dbQuantity > minQty) {
        // Stock is healthy again - automatically remove from shopping list
        const { error: updateError } = await supabase
          .from('inventory_items')
          .update({ on_shopping_list: false })
          .eq('ean', ean);

        if (!updateError) {
          isOnShoppingList = false;
          removedFromShoppingList = true;
        } else {
          console.warn('Failed to auto-remove from shopping list:', updateError);
        }
      }

      // CRITICAL: Invalidate ALL inventory cache entries after successful transaction
      // This ensures that fetchAllData will fetch fresh data on next call
      // We invalidate all pages because pagination might affect which items are visible
      cacheService.invalidate('inventory:');
      console.log('[updateItemQuantity] Cleared inventory cache entries');

      // Also clear localStorage cache to ensure fresh data
      try {
        localStorage.removeItem(CACHE_KEY_INVENTORY);
        console.log('[updateItemQuantity] Cleared localStorage inventory cache');
      } catch (e) {
        console.warn('[updateItemQuantity] Failed to clear localStorage cache:', e);
      }

      // Return full item structure with low_stock flag if applicable
      return {
        ...result,
        new_quantity: dbQuantity, // Use actual DB quantity, not RPC result
        min_quantity: minQty,
        on_shopping_list: isOnShoppingList,
        product_name: productName,
        low_stock: isLowStock, // Always set this flag if low stock
        just_crossed: justCrossedThreshold, // Flag to indicate we just crossed threshold
        removed_from_shopping_list: removedFromShoppingList, // Flag to indicate auto-removal
      } as TransactionResult & {
        min_quantity: number;
        on_shopping_list: boolean;
        product_name: string;
        low_stock: boolean;
        just_crossed?: boolean;
        removed_from_shopping_list?: boolean;
      };
    } catch (error) {
      // If not syncing and ANY error occurred (including Auth/Token errors), add to queue
      if (!isSyncing) {
        this.addToQueue({
          type: 'updateItemQuantity',
          data: {
            ean,
            delta,
            productDetails,
            reason,
          },
        });
        console.log('Error occurred - added to sync queue:', error);
      }
      // Re-throw error so caller can handle it
      throw error;
    }
  },

  // 4. ADATBÁZIS TÖRLÉSE (Csak darabszámok)
  async clearInventoryValues(
    logAction?: (actionType: string, details: string, isSyncing: boolean) => Promise<void>
  ) {
    // Get user with offline fallback
    let user = null;
    try {
      const { data, error: authError } = await supabase.auth.getUser();
      if (data?.user) {
        user = data.user;
      }
    } catch (error) {
      // Auth check failed, try cached user
    }

    // Fallback to cached user if auth failed (offline mode)
    if (!user) {
      const cachedUserStr = localStorage.getItem('offline_user');
      if (cachedUserStr) {
        try {
          const cachedUser = JSON.parse(cachedUserStr);
          user = { id: cachedUser.id } as any;
        } catch (e) {
          // Failed to parse cached user
        }
      }
    }

    if (!user) return;

    // Get company_id from profile to reset all company inventory (global shop reset)
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', user.id)
      .single();

    if (profileError) {
      console.error('Error fetching company_id:', profileError);
      throw new Error('Hiba a vállalat azonosító lekérdezésekor!');
    }

    const companyId = profile?.company_id;
    if (!companyId) {
      throw new Error('Nincs vállalat azonosító!');
    }

    // Reset all inventory quantities for the entire company (all terminals)
    const { error } = await supabase
      .from('inventory_items')
      .update({ quantity: 0 })
      .eq('company_id', companyId);

    if (error) throw error;

    // Naplózzuk a törlést is
    if (logAction) {
      await logAction('RESET', 'Leltár nullázva (összes terminál)', false);
    }
  },

  // 7. EGYETLEN TÉTEL TÖRLÉSE
  async deleteItem(
    ean: string,
    logAction?: (actionType: string, details: string, isSyncing: boolean) => Promise<void>
  ) {
    // Get user with offline fallback
    let user = null;
    try {
      const { data, error: authError } = await supabase.auth.getUser();
      if (data?.user) {
        user = data.user;
      }
    } catch (error) {
      // Auth check failed, try cached user
    }

    // Fallback to cached user if auth failed (offline mode)
    if (!user) {
      const cachedUserStr = localStorage.getItem('offline_user');
      if (cachedUserStr) {
        try {
          const cachedUser = JSON.parse(cachedUserStr);
          user = { id: cachedUser.id } as any;
        } catch (e) {
          // Failed to parse cached user
        }
      }
    }

    if (!user) throw new Error('Nincs bejelentkezve');

    // Get company_id from profile to ensure company-level deletion
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', user.id)
      .single();

    if (profileError) {
      console.error('Error fetching company_id:', profileError);
      throw new Error('Hiba a vállalat azonosító lekérdezésekor!');
    }

    const companyId = profile?.company_id;

    // Delete from inventory_items
    // Use company_id instead of user_id to allow deletion of items from same company
    // This fixes deletion of old items that may have different user_id
    const { error: inventoryError } = await supabase
      .from('inventory_items')
      .delete()
      .eq('company_id', companyId)
      .eq('ean', ean);

    if (inventoryError) {
      console.error('Error deleting from inventory_items:', inventoryError);
      throw inventoryError;
    }

    // NOTE: supplier_products deletion removed - this table does not exist in prod schema
    // Only inventory_items needs to be deleted

    // ===================================================================
    // ✅ CRITICAL: INVALIDATE CACHE after successful delete
    // ===================================================================
    cacheService.invalidate('inventory:');
    console.log('[deleteItem] Cleared inventory cache entries');

    try {
      localStorage.removeItem(CACHE_KEY_INVENTORY);
      console.log('[deleteItem] Cleared localStorage inventory cache');
    } catch (e) {
      console.warn('[deleteItem] Failed to clear localStorage cache:', e);
    }

    if (logAction) {
      await logAction('RESET', `Tétel törölve: ${ean}`, false);
    }
  },

  // 7.5. TÉTEL ÚJRA LÉTREHOZÁSA (Undo funkcióhoz)
  async recreateItem(
    itemData: {
      supplier: SupplierProduct;
      inventory: { ean: string; quantity: number; scannedAt?: Date };
    },
    logAction?: (actionType: string, details: string, isSyncing: boolean) => Promise<void>
  ) {
    // Get user with offline fallback
    let user = null;
    try {
      const { data, error: authError } = await supabase.auth.getUser();
      if (data?.user) {
        user = data.user;
      }
    } catch (error) {
      // Auth check failed, try cached user
    }

    // Fallback to cached user if auth failed (offline mode)
    if (!user) {
      const cachedUserStr = localStorage.getItem('offline_user');
      if (cachedUserStr) {
        try {
          const cachedUser = JSON.parse(cachedUserStr);
          user = { id: cachedUser.id } as any;
        } catch (e) {
          // Failed to parse cached user
        }
      }
    }

    if (!user) throw new Error('Nincs bejelentkezve');

    const { ean } = itemData.inventory;

    // Get company_id from profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', user.id)
      .single();

    if (profileError) {
      console.error('Error fetching company_id:', profileError);
      throw new Error('Hiba a vállalat azonosító lekérdezésekor!');
    }

    const companyId = profile?.company_id;

    // Calculate Hybrid Pricing (Net → Gross)
    const vatRate = itemData.supplier.vatRate ?? 27;
    const purchaseNet = itemData.supplier.netPrice || 0;
    const sellingNet = itemData.supplier.netSellingPrice || 0;
    const purchaseGross =
      purchaseNet > 0 ? Math.round(purchaseNet * (1 + vatRate / 100) * 100) / 100 : 0;
    const sellingGross =
      sellingNet > 0
        ? Math.round(sellingNet * (1 + vatRate / 100) * 100) / 100
        : itemData.supplier.sellingPrice || 0;

    // Recreate inventory item with all product data
    const { error: inventoryError } = await supabase.from('inventory_items').upsert(
      {
        ean: ean,
        user_id: user.id,
        company_id: companyId,
        name: itemData.supplier.name,
        sku: itemData.supplier.sku || '',
        quantity: itemData.inventory.quantity,
        last_scanned_at: itemData.inventory.scannedAt?.toISOString() || new Date().toISOString(),
        // Gross values (for POS backward compatibility)
        price: purchaseGross,
        selling_price: sellingGross,
        // Net values (for profit calculations)
        net_price: purchaseNet,
        net_selling_price: sellingNet,
        vat_rate: vatRate,
        // Weight fields
        is_weighable: itemData.supplier.isWeighable || false,
        unit_weight: itemData.supplier.unitWeight || 0,
        unit: itemData.supplier.unit || 'db',
      },
      {
        onConflict: 'ean,user_id',
      }
    );

    if (inventoryError) throw inventoryError;

    // ===================================================================
    // ✅ CRITICAL: INVALIDATE CACHE after successful recreate
    // ===================================================================
    cacheService.invalidate('inventory:');
    console.log('[recreateItem] Cleared inventory cache entries');

    try {
      localStorage.removeItem(CACHE_KEY_INVENTORY);
      console.log('[recreateItem] Cleared localStorage inventory cache');
    } catch (e) {
      console.warn('[recreateItem] Failed to clear localStorage cache:', e);
    }

    if (logAction) {
      await logAction('RESTORE', `Tétel visszaállítva: ${ean}`, false);
    }
  },

  // 8. EGYETLEN TÉTEL SZERKESZTÉSE (DEBUG VERZIÓ)
  async editItem(
    ean: string,
    updates: {
      name: string;
      price: number;
      quantity: number;
      unitWeight?: number;
      isWeighable?: boolean;
      selling_price?: number;
      unit?: 'db' | 'm' | 'kg';
      minQuantity?: number | null;
      netPrice?: number;
      netSellingPrice?: number;
      vatRate?: number;
    }
  ) {
    // 1. DEBUG: Lássuk, mit kapott a függvény!
    console.group(`🔧 editItem DEBUG: ${ean}`);
    console.log('1. Bejövő adatok (updates):', JSON.stringify(updates, null, 2));

    // ===================================================================
    // 🌐 TRY ONLINE OPERATION FIRST (like POS mode)
    // If network error occurs, fall back to offline queue
    // ===================================================================
    try {
      // Get user with offline fallback
      let user = null;
      try {
        const { data, error: authError } = await supabase.auth.getUser();
        if (data?.user) {
          user = data.user;
        }
      } catch (error) {
        // Auth check failed, try cached user
      }

      // Fallback to cached user if auth failed (offline mode)
      if (!user) {
        const cachedUserStr = localStorage.getItem('offline_user');
        const cachedUser = safeParseOfflineUser(cachedUserStr);
        if (cachedUser) {
          user = { id: cachedUser.id } as any;
        }
      }

      if (!user) throw new Error('Nincs bejelentkezve');

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (profileError) {
        // Check if it's a network error
        const isNetworkError =
          profileError.message?.includes('fetch') ||
          profileError.message?.includes('Failed to fetch') ||
          profileError.message?.includes('NetworkError') ||
          profileError.message?.includes('ERR_') ||
          profileError.message?.includes('ENOTFOUND') ||
          profileError.message?.includes('ECONNREFUSED') ||
          profileError.code === 'PGRST301' ||
          profileError.code === 'PGRST000';

        if (isNetworkError) {
          console.warn('🌐❌ Network error fetching profile - falling back to offline mode');
          throw new Error('NETWORK_ERROR');
        }

        console.error('Error fetching company_id:', profileError);
        throw new Error('Hiba a vállalat azonosító lekérdezésekor!');
      }

      const companyId = profile?.company_id;

      // 2. Számítási logika
      const vatRate = updates.vatRate ?? 27;

      let purchaseNet: number | undefined = updates.netPrice;
      let purchaseGross: number | undefined = updates.price;
      let sellingNet = updates.netSellingPrice;
      let sellingGross = updates.selling_price ?? 0;

      // DEBUG: Kiinduló állapot
      console.log('2. Kiinduló árak:', { purchaseNet, purchaseGross, vatRate });

      // CRITICAL FIX: Prioritize NET price as source of truth
      // If netPrice is provided, USE IT to calculate Gross (prevents price shrinking bug)
      if (purchaseNet !== undefined && purchaseNet !== null) {
        console.log('-> Nettó ár megadva, ebből számolunk Bruttót.');
        purchaseNet = Math.round(purchaseNet);
        // Recalculate Gross from Net to ensure consistency
        purchaseGross =
          purchaseNet >= 0 ? Math.round(purchaseNet * (1 + vatRate / 100) * 100) / 100 : 0;
      } else if (purchaseGross !== undefined && purchaseGross !== null) {
        // Fallback: Calculate Net from Gross (only if Net was not provided)
        console.log('-> Csak Bruttó ár van, visszaszámolunk Nettót.');
        purchaseNet =
          purchaseGross > 0 ? Math.round((purchaseGross / (1 + vatRate / 100)) * 100) / 100 : 0;
      } else {
        // Neither provided - set defaults
        purchaseNet = 0;
        purchaseGross = 0;
      }

      // Eladási ár logika:
      // A workflow szerint a BRUTTÓ az elsődleges mező, ezért ezt tartjuk meg.
      if (sellingGross !== undefined && sellingGross !== null) {
        sellingGross = Math.round(sellingGross);
        sellingNet =
          sellingGross > 0 ? Math.round((sellingGross / (1 + vatRate / 100)) * 100) / 100 : 0;
      } else if (sellingNet !== undefined && sellingNet !== null) {
        sellingGross = sellingNet >= 0 ? Math.round(sellingNet * (1 + vatRate / 100)) : 0;
      }

      // DEBUG: Számított értékek
      console.log('3. Számított végleges árak:', {
        FINAL_NET_PRICE: purchaseNet,
        FINAL_GROSS_PRICE: purchaseGross,
      });

      // 3. Payload összeállítása
      const updatePayload: any = {
        name: updates.name,
        price: purchaseGross,
        quantity: updates.quantity,
        unit_weight: updates.unitWeight || 0,
        is_weighable: updates.isWeighable || false,
        // ✅ HOTFIX: Remove local timestamp - let DB generate server timestamp
        // This prevents clock skew issues (local clock may be ahead of server)
        // last_scanned_at: new Date().toISOString(), // REMOVED - DB will auto-update

        // A KRITIKUS MEZŐK:
        net_price: purchaseNet,
        net_selling_price: sellingNet,
        vat_rate: vatRate,
      };

      if (sellingGross >= 0) updatePayload.selling_price = sellingGross;
      if (updates.unit !== undefined) updatePayload.unit = updates.unit;
      if (updates.minQuantity !== undefined) updatePayload.min_quantity = updates.minQuantity;

      // DEBUG: Mit küldünk az adatbázisnak?
      console.log('4. DB Payload (Ez megy a szerverre):', JSON.stringify(updatePayload, null, 2));
      console.groupEnd();

      // 4. Adatbázis művelet (UPDATE) - Request server timestamp back
      let { data: updatedData, error: updateError } = await supabase
        .from('inventory_items')
        .update(updatePayload)
        .eq('company_id', companyId)
        .eq('ean', ean)
        .select('last_scanned_at, quantity, min_quantity, net_price, net_selling_price, vat_rate'); // ✅ Request server data back (removed updated_at - column does not exist)

      if (updateError) {
        console.error('DB UPDATE Error:', updateError);
        throw updateError;
      }

      // 5. Ha nincs update, akkor INSERT
      if (!updatedData || updatedData.length === 0) {
        console.log('Update nem talált sort, INSERT következik...');
        const insertPayload = {
          ...updatePayload,
          user_id: user.id,
          company_id: companyId,
          ean: ean,
        };

        const { data: insertedData, error: insertError } = await supabase
          .from('inventory_items')
          .insert(insertPayload)
          .select('last_scanned_at'); // ✅ Request server timestamp (removed updated_at - column does not exist)
        if (insertError) {
          console.error('DB INSERT Error:', insertError);
          throw insertError;
        }
        // Use inserted data for timestamp
        if (insertedData && insertedData.length > 0) {
          updatedData = insertedData;
        }
      }

      // ===================================================================
      // ✅ CRITICAL: INVALIDATE CACHE after successful DB write
      // ===================================================================
      // This ensures that fetchAllData will fetch fresh data on next call
      // Without this, loadCloudData() gets stale cached data (causes UI to show old values)
      cacheService.invalidate('inventory:');
      console.log('[editItem] Cleared inventory cache entries');

      // Also clear localStorage cache to ensure fresh data
      try {
        localStorage.removeItem(CACHE_KEY_INVENTORY);
        console.log('[editItem] Cleared localStorage inventory cache');
      } catch (e) {
        console.warn('[editItem] Failed to clear localStorage cache:', e);
      }

      // ===================================================================
      // ✅ ONLINE SUCCESS - Extract server timestamp and return it
      // ===================================================================
      const serverTimestamp = updatedData?.[0]?.last_scanned_at || new Date().toISOString();
      console.log(
        `✅ Online editItem completed successfully - Server timestamp: ${serverTimestamp} (local would be: ${new Date().toISOString()})`
      );

      // ✅ CRITICAL: Return server timestamp so caller (App.tsx handleUpdateItem) can use it
      return {
        success: true,
        serverTimestamp: serverTimestamp,
        data: updatedData?.[0],
      };
    } catch (error: any) {
      // ===================================================================
      // 🔥 CATCH NETWORK ERRORS → Queue for offline sync (like POS mode)
      // ===================================================================
      const isNetworkError =
        error.message === 'NETWORK_ERROR' ||
        error.message?.includes('fetch') ||
        error.message?.includes('Failed to fetch') ||
        error.message?.includes('NetworkError') ||
        error.message?.includes('ERR_') ||
        error.message?.includes('ENOTFOUND') ||
        error.message?.includes('ECONNREFUSED') ||
        error.code === 'PGRST301' ||
        error.code === 'PGRST000';

      if (isNetworkError) {
        console.warn('🌐❌ Network error detected - Queueing editItem for offline sync');

        // Get user from cache (we're offline, so can't fetch from DB)
        const cachedUserStr = localStorage.getItem('offline_user');
        let userId: string | null = null;
        const cachedUser = safeParseOfflineUser(cachedUserStr);
        if (cachedUser) {
          userId = cachedUser.id;
        }

        if (!userId) {
          console.error('No cached user found - cannot queue operation');
          throw new Error('Nincs bejelentkezve (offline mode)');
        }

        // Calculate prices (same logic as online mode)
        const vatRate = updates.vatRate ?? 27;
        let purchaseNet: number | undefined = updates.netPrice;
        let purchaseGross: number | undefined = updates.price;
        let sellingNet = updates.netSellingPrice;
        let sellingGross = updates.selling_price ?? 0;

        if (purchaseNet !== undefined && purchaseNet !== null) {
          purchaseGross =
            purchaseNet >= 0 ? Math.round(purchaseNet * (1 + vatRate / 100) * 100) / 100 : 0;
        } else if (purchaseGross !== undefined && purchaseGross !== null) {
          purchaseNet =
            purchaseGross > 0 ? Math.round((purchaseGross / (1 + vatRate / 100)) * 100) / 100 : 0;
        } else {
          purchaseNet = 0;
          purchaseGross = 0;
        }

        if (sellingNet !== undefined && sellingNet !== null) {
          sellingGross =
            sellingNet >= 0 ? Math.round(sellingNet * (1 + vatRate / 100) * 100) / 100 : 0;
        } else if (
          sellingGross !== undefined &&
          sellingGross !== null &&
          sellingNet === undefined
        ) {
          sellingNet =
            sellingGross > 0 ? Math.round((sellingGross / (1 + vatRate / 100)) * 100) / 100 : 0;
        }

        // ✅ CRITICAL: Invalidate memory cache (cacheService) FIRST
        // This ensures loadCloudData() won't use stale cached data
        cacheService.invalidate('inventory:');
        console.log('[editItem] Cleared memory cache (offline mode)');

        // Update localStorage cache immediately (optimistic update)
        try {
          const cachedInventory = localStorage.getItem(CACHE_KEY_INVENTORY);
          if (cachedInventory) {
            const inventoryMap = safeJSONParse(cachedInventory, {});
            if (inventoryMap[ean]) {
              inventoryMap[ean].quantity = updates.quantity;
              // ✅ HOTFIX: Use current time for offline cache (will be corrected on sync)
              inventoryMap[ean].scannedAt = new Date().toISOString();
              if (updates.minQuantity !== undefined) {
                inventoryMap[ean].min_quantity = updates.minQuantity;
              }
              localStorage.setItem(CACHE_KEY_INVENTORY, JSON.stringify(inventoryMap));
              console.log(
                `✅ Updated local cache for ${ean}, quantity: ${updates.quantity} (offline mode)`
              );
            }
          }

          // Update supplier cache as well
          const cachedSupplier = localStorage.getItem(CACHE_KEY_SUPPLIER);
          if (cachedSupplier) {
            const supplierMap = safeJSONParse(cachedSupplier, {});
            if (supplierMap[ean]) {
              supplierMap[ean].name = updates.name;
              if (purchaseNet !== undefined) supplierMap[ean].netPrice = purchaseNet;
              if (sellingNet !== undefined) supplierMap[ean].netSellingPrice = sellingNet;
              if (sellingGross !== undefined) supplierMap[ean].sellingPrice = sellingGross;
              if (vatRate !== undefined) supplierMap[ean].vatRate = vatRate;
              if (updates.isWeighable !== undefined)
                supplierMap[ean].isWeighable = updates.isWeighable;
              if (updates.unitWeight !== undefined)
                supplierMap[ean].unitWeight = updates.unitWeight;
              if (updates.unit !== undefined) supplierMap[ean].unit = updates.unit;
              localStorage.setItem(CACHE_KEY_SUPPLIER, JSON.stringify(supplierMap));
              console.log(`✅ Updated supplier cache for ${ean}`);
            }
          }
        } catch (cacheError) {
          console.warn('Failed to update local cache:', cacheError);
        }

        // Queue the operation for later sync using process_inventory_transaction RPC
        const itemPayload = {
          ean: ean,
          quantity: updates.quantity,
          price: purchaseGross,
          selling_price: sellingGross,
          net_price: purchaseNet,
          net_selling_price: sellingNet,
          vat_rate: vatRate,
          name: updates.name,
          unit_weight: updates.unitWeight,
          is_weighable: updates.isWeighable,
          unit: updates.unit || 'db',
        };

        const rpcPayload = {
          p_user_id: userId,
          p_type: 'COUNT', // COUNT type = absolute quantity set
          p_notes: `Offline edit: ${updates.name}`,
          p_items: [itemPayload],
        };

        await syncService.addToQueue('process_inventory_transaction', rpcPayload, 'COUNT');
        console.log('📦 Queued editItem operation for later sync (COUNT transaction)');
        console.groupEnd();

        // ✅ HOTFIX: Return success with local timestamp (optimistic - will be corrected on sync)
        return {
          success: true,
          serverTimestamp: new Date().toISOString(),
          data: null,
          offline: true,
        };
      }

      // If it's not a network error, re-throw (business logic error)
      console.groupEnd();
      throw error;
    }
  },

  // Toggle shopping list status for an item
  async toggleShoppingList(ean: string, onShoppingList: boolean) {
    // Get user with offline fallback
    let user = null;
    try {
      const { data, error: authError } = await supabase.auth.getUser();
      if (data?.user) {
        user = data.user;
      }
    } catch (error) {
      // Auth check failed, try cached user
    }

    // Fallback to cached user if auth failed (offline mode)
    if (!user) {
      const cachedUserStr = localStorage.getItem('offline_user');
      if (cachedUserStr) {
        try {
          const cachedUser = JSON.parse(cachedUserStr);
          user = { id: cachedUser.id } as any;
        } catch (e) {
          // Failed to parse cached user
        }
      }
    }

    if (!user) throw new Error('User not authenticated');

    // Update and return full item data including min_quantity and on_shopping_list
    const { data, error } = await supabase
      .from('inventory_items')
      .update({ on_shopping_list: onShoppingList })
      .eq('ean', ean)
      .select('ean, quantity, min_quantity, on_shopping_list, last_scanned_at')
      .single();

    if (error) throw error;
    return data;
  },

  // Update min_quantity for an item (null = no limit, 0+ = valid limit)
  // FIXED: Added user_id filter to prevent .single() failure in multi-user companies
  // FIXED: Changed .single() to .maybeSingle() to avoid throwing on 0 or multiple rows
  async updateMinQuantity(ean: string, minQuantity: number | null) {
    // Get user with offline fallback
    let user = null;
    try {
      const { data, error: authError } = await supabase.auth.getUser();
      if (data?.user) {
        user = data.user;
      }
    } catch (error) {
      // Auth check failed, try cached user
    }

    // Fallback to cached user if auth failed (offline mode)
    if (!user) {
      const cachedUserStr = localStorage.getItem('offline_user');
      if (cachedUserStr) {
        try {
          const cachedUser = JSON.parse(cachedUserStr);
          user = { id: cachedUser.id } as any;
        } catch (e) {
          // Failed to parse cached user
        }
      }
    }

    if (!user) throw new Error('User not authenticated');

    // First try company-wide update (preferred for shared inventory)
    const { data: profile } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', user.id)
      .maybeSingle();

    const companyId = profile?.company_id;

    let data, error;

    if (companyId) {
      // Company mode: filter by company_id + ean (matches editItem pattern)
      ({ data, error } = await supabase
        .from('inventory_items')
        .update({ min_quantity: minQuantity })
        .eq('company_id', companyId)
        .eq('ean', ean)
        .select());
    } else {
      // Solo mode: filter by user_id + ean
      ({ data, error } = await supabase
        .from('inventory_items')
        .update({ min_quantity: minQuantity })
        .eq('user_id', user.id)
        .eq('ean', ean)
        .select());
    }

    if (error) throw error;
    return data;
  },

  /**
   * Fallback lookup: Get product by EAN from database (for scanner when not in paginated results)
   * Returns both supplier product details and inventory quantity
   */
  async getProductByEan(ean: string): Promise<{
    supplier: SupplierProduct;
    inventory: { quantity: number; scannedAt: Date };
  } | null> {
    try {
      // Get user with offline fallback (same as posService and syncService)
      let user = null;
      try {
        const { data, error: authError } = await supabase.auth.getUser();
        if (data?.user) {
          user = data.user;
        }
      } catch (error) {
        // Auth check failed, try cached user
      }

      // Fallback to cached user if auth failed (offline mode)
      if (!user) {
        const cachedUserStr = localStorage.getItem('offline_user');
        const cachedUser = safeParseOfflineUser(cachedUserStr);
        if (cachedUser) {
          user = { id: cachedUser.id } as any;
        }
      }

      if (!user) {
        return null;
      }

      // Get current user's company_id
      const { data: currentUserProfile, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .eq('is_active', true)
        .single();

      let query = supabase
        .from('inventory_items')
        .select(
          'ean, name, sku, price, selling_price, net_price, net_selling_price, vat_rate, quantity, last_scanned_at, unit_weight, is_weighable, unit'
        )
        .eq('ean', ean);

      // Filter by company_id if available
      if (!profileError && currentUserProfile?.company_id) {
        query = query.eq('company_id', currentUserProfile.company_id);
      }

      const { data, error } = await query.limit(1).maybeSingle();

      if (error || !data) {
        return null;
      }

      // Map DB columns (snake_case) to TypeScript properties (camelCase)
      return {
        supplier: {
          ean: data.ean,
          name: data.name || 'Ismeretlen',
          sku: data.sku || '',
          netPrice: Number(data.net_price) || Number(data.price) || 0,
          sellingPrice: Number(data.selling_price) || 0,
          netSellingPrice: Number(data.net_selling_price) || Number(data.selling_price) || 0,
          vatRate: Number(data.vat_rate) || 27,
          isWeighable: data.is_weighable ?? false,
          unitWeight: data.unit_weight ? Number(data.unit_weight) : undefined,
          unit: data.unit || 'db',
        },
        inventory: {
          quantity: data.quantity ?? 0,
          scannedAt: data.last_scanned_at ? new Date(data.last_scanned_at) : new Date(),
        },
      };
    } catch (error) {
      return null;
    }
  },

  /**
   * Processes inventory transaction batch (for SCRAP/INTERNAL cart-based transactions)
   * Uses process_inventory_transaction RPC with multiple items
   */
  async processInventoryBatch(
    transactionType: 'SCRAP' | 'INTERNAL',
    cartItems: Array<{
      ean: string;
      name: string;
      quantity: number;
      price?: number;
      selling_price?: number;
      unit?: 'db' | 'm' | 'kg';
      unit_weight?: number;
      is_weighable?: boolean;
    }>,
    sellerName?: string,
    notes?: string
  ): Promise<{
    transaction_id: string;
    updated_items: Array<{ ean: string; new_quantity: number; updated_at: string }>;
  }> {
    try {
      // Get user with offline fallback
      let user = null;
      try {
        const { data, error: authError } = await supabase.auth.getUser();
        if (data?.user) {
          user = data.user;
        }
      } catch (error) {
        // Auth check failed, try cached user
      }

      // Fallback to cached user if auth failed (offline mode)
      if (!user) {
        const cachedUserStr = localStorage.getItem('offline_user');
        const cachedUser = safeParseOfflineUser(cachedUserStr);
        if (cachedUser) {
          user = { id: cachedUser.id } as any;
        }
      }

      if (!user) {
        throw new Error('User not authenticated');
      }

      // Prepare items for RPC
      const itemsJson = cartItems.map((item) => ({
        ean: item.ean,
        name: item.name,
        quantity: item.quantity,
        price: item.price,
        selling_price: item.selling_price,
        unit: item.unit || 'db',
        unit_weight: item.unit_weight,
        is_weighable: item.is_weighable,
      }));

      // Build notes with seller name if provided
      const finalNotes = sellerName
        ? `Felhasználó: ${sellerName}${notes ? ' | ' + notes : ''}`
        : notes || null;

      // Call RPC function for batch processing (V2 version)
      const { data, error } = await supabase.rpc(
        getRpcNameFromUtil(RPC_FUNCTIONS.PROCESS_INVENTORY_TRANSACTION),
        {
          p_user_id: user.id,
          p_type: transactionType,
          p_notes: finalNotes,
          p_items: itemsJson,
        }
      );

      if (error) {
        console.error('RPC process_inventory_transaction batch error:', error);
        throw new Error(error.message || 'Inventory transaction batch failed');
      }

      if (!data) {
        throw new Error('No data returned from inventory transaction');
      }

      // Handle different response formats
      let updated_items: Array<{ ean: string; new_quantity: number; updated_at: string }> = [];
      let transaction_id = '';

      // Check if data is an array (TABLE return type)
      if (Array.isArray(data)) {
        // RPC returns TABLE - array of records
        const response = data as Array<{
          transaction_id: string;
          ean?: string;
          item_ean?: string; // Some versions use item_ean instead of ean
          new_quantity: number;
          updated_at: string;
        }>;

        if (response.length === 0) {
          throw new Error('No items returned from inventory transaction');
        }

        // Extract transaction_id from first item (all items share the same transaction)
        transaction_id = response[0]?.transaction_id || '';

        // Map to expected format
        updated_items = response.map((item) => ({
          ean: item.ean || item.item_ean || '',
          new_quantity: item.new_quantity,
          updated_at: item.updated_at,
        }));
      } else if (typeof data === 'object' && data !== null) {
        // Handle object response format (if RPC returns wrapped object)
        const response = data as {
          transaction_id?: string;
          updated_items?: Array<{ ean: string; new_quantity: number; updated_at: string }>;
          id?: string;
        };

        transaction_id = response.transaction_id || response.id || '';

        if (response.updated_items && Array.isArray(response.updated_items)) {
          updated_items = response.updated_items;
        } else {
          // If no updated_items array, try to extract from single object
          const singleItem = data as {
            ean?: string;
            item_ean?: string;
            new_quantity?: number;
            updated_at?: string;
          };

          if (singleItem.ean || singleItem.item_ean) {
            updated_items = [
              {
                ean: singleItem.ean || singleItem.item_ean || '',
                new_quantity: singleItem.new_quantity || 0,
                updated_at: singleItem.updated_at || new Date().toISOString(),
              },
            ];
          }
        }
      } else {
        // Unexpected format - log and throw
        console.error('[processInventoryBatch] Unexpected response format:', typeof data, data);
        throw new Error('Unexpected response format from inventory transaction');
      }

      if (updated_items.length === 0) {
        throw new Error('No items returned from inventory transaction');
      }

      // ===================================================================
      // ✅ CRITICAL: INVALIDATE CACHE after successful DB write
      // ===================================================================
      // This ensures that fetchAllData will fetch fresh data on next call
      // Without this, loadCloudData() gets stale cached data (causes UI to show old values)
      cacheService.invalidate('inventory:');
      console.log('[processInventoryBatch] Cleared inventory cache entries');

      // Also clear localStorage cache to ensure fresh data
      try {
        localStorage.removeItem(CACHE_KEY_INVENTORY);
        console.log('[processInventoryBatch] Cleared localStorage inventory cache');
      } catch (e) {
        console.warn('[processInventoryBatch] Failed to clear localStorage cache:', e);
      }

      return {
        transaction_id,
        updated_items,
      };
    } catch (error: any) {
      console.error('[processInventoryBatch] ❌ Error:', error);
      throw error;
    }
  },

  /**
   * Fetches global inventory statistics from the server using RPC function
   * Returns server-side calculated stats (fixes pagination issues)
   */
  async getInventoryStats(): Promise<InventoryGlobalStats> {
    try {
      const rpcName = getRpcName('get_inventory_stats');
      const { data, error } = await supabase.rpc(rpcName);

      if (error) {
        console.error('[getInventoryStats] Error:', error);
        // Return explicit 0 values on error
        return {
          stock_value: 0,
          counted_items: 0,
          total_items: 0,
          not_scanned: 0,
          unknown: 0,
          low_stock_count: 0,
          shopping_list_count: 0,
        };
      }

      // Ensure we have valid data structure
      if (!data || typeof data !== 'object') {
        console.warn('[getInventoryStats] Invalid data structure:', data);
        return {
          stock_value: 0,
          counted_items: 0,
          total_items: 0,
          not_scanned: 0,
          unknown: 0,
          low_stock_count: 0,
          shopping_list_count: 0,
        };
      }

      // RPC can return single object (jsonb) or one-row array (RETURNS TABLE after 009/006)
      const row = Array.isArray(data) && data.length > 0 ? data[0] : data;

      return {
        stock_value: Number(row.stock_value) || 0,
        counted_items: Number(row.counted_items) || 0,
        total_items: Number(row.total_items) || 0,
        not_scanned: Number(row.not_scanned) || 0,
        unknown: Number(row.unknown) || 0,
        low_stock_count: Number(row.low_stock_count) || 0,
        shopping_list_count: Number(row.shopping_list_count) || 0,
      };
    } catch (error: any) {
      console.error('[getInventoryStats] Exception:', error);
      // Return explicit 0 values on exception
      return {
        stock_value: 0,
        counted_items: 0,
        total_items: 0,
        not_scanned: 0,
        unknown: 0,
        low_stock_count: 0,
        shopping_list_count: 0,
      };
    }
  },
};
